<template>
  <a-modal
    v-model="showModal"
    :title="isEdit ? 'Edit rule' : 'Add rule'"
    :footer="null"
    width="600px"
    :destroyOnClose="true"
    class="rule-modal"
  >
    <a-form-model class="" ref="ruleForm" :model="form" :rules="rules">
      <a-row class="d-flex">
        <a-form-model-item
          label="Image"
          prop="image"
          :colon="false"
          style="max-width: 166px"
          class="mr-5"
        >
          <div class="rule-modal__image-container">
            <a-tooltip
              placement="top"
              arrow-point-at-center
            >
              <template v-if="form.imageUrl" slot="image">
                <span>File size 288 x 288 px<br />(jpg, jpeg, png, webp)</span>
              </template>
              <a-upload
                list-type="picture-card"
                action="#"
                class="avatar-uploader d-flex justify-content-center"
                :show-upload-list="false"
                accept=".png,.jpg,.jpeg,.webp"
                :disabled="loading || imageLoading"
                :before-upload="beforeUpload"
                @change="imgChange"
              >
                <img
                  v-if="form.imageUrl"
                  :src="form.imageUrl"
                  alt="Rule image"
                  style="width: 150px; height: 150px; border-radius: 4px;"
                />
                <div v-else>
                  <a-icon :type="loading ? 'loading' : 'inbox'" class="text-primary" style="font-size: 48px" />
                  <div class="ant-upload-text" style="color: #8C8C8C; font-weight: 300; font-size: 11px;">
                    File size 288 x 288 px<br/> ( jpg, jpeg, png, webp )
                  </div>
                </div>
              </a-upload>
            </a-tooltip>
          </div>
        </a-form-model-item>
        <div class="d-flex flex-column" style="width: 100%;">
          <a-form-model-item label="Title" prop="name" :colon="false">
            <a-input
              v-model="form.name"
              placeholder="Title"
              size="large"
              :disabled="loading"
            />
          </a-form-model-item>
          <a-form-model-item label="Description" prop="description" :colon="false">
            <a-textarea
              v-model="form.description"
              placeholder="Description"
              size="large"
              :auto-size="{ minRows: 2, maxRows: 6 }"
              :disabled="loading"
            />
          </a-form-model-item>
        </div>
      </a-row>
      <a-row class="footer-viewscreen footer">
        <div class="w-fit d-flex justify-content-start">
          <a-popconfirm
            v-if="isEdit"
            placement="top"
            :disabled="loading"
            title="Are you sure you want to delete this rule?"
            okText="Delete"
            okType="danger"
            @confirm="deleteRule"
          >
            <a-button type="danger">
              <a-icon type="delete" theme="filled" />
            </a-button>
          </a-popconfirm>
        </div>
        <div class="d-flex justify-content-end">
          <a-button
            :loading="loading"
            @click="closeModal"
          >
            Cancel
          </a-button>
          <a-button
            type="primary"
            class="ml-2"
            :loading="loading"
            @click="onSubmit"
          >
            {{ isEdit ? 'Save' : 'Create' }}
          </a-button>
        </div>
      </a-row>
    </a-form-model>
  </a-modal>
</template>

<script>
import { beforeUpload, getBase64 } from '@/composables/useUploadImage.js'

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedRule: {
      type: Object,
      default: null,
    },
  },
  components: {
  },
  data: () => ({
    showModal: false,
    form: {
      name: '',
      description: '',
      image: undefined,
      imageUrl: '',
    },
    rules: {
      image: [
        {
          required: true,
          message: 'Image is required',
          trigger: ['change'],
        },
      ],
      name: [
        {
          required: true,
          message: 'Title is required',
          trigger: ['change'],
        },
      ],
      description: [
        {
          required: true,
          message: 'Description is required',
          trigger: ['change'],
        },
      ],
    },
    imageLoading: false,
    loading: false,
  }),
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.showModal = true
        } else {
          this.showModal = false
        }
      },
      deep: true,
      immediate: true,
    },
    showModal: {
      handler(val) {
        if (!val) {
          this.closeModal()
        }
      },
      deep: true,
    },
  },
  computed: {
    isEdit() {
      return !!this.selectedRule
    },
  },
  methods: {
    templateDataFilling() {
      this.form.name = this.selectedRule.name
      this.form.description = this.selectedRule.description
      this.form.image = this.selectedRule.image
      this.form.imageUrl = this.selectedRule.imageUrl
    },
    async beforeUpload(file) {
      return await beforeUpload(this, file, 2, 288)
    },
    imgChange(info) {
      if (info.file.status === 'uploading') {
        this.imageLoading = true
        return
      }

      info.file.status = 'done'

      this.form.image = info.file.originFileObj
      getBase64(info.file.originFileObj, imageUrl => {
        this.form.imageUrl = imageUrl
        this.imageLoading = false
        this.$refs.ruleForm.validateField('image')
      })
    },
    onSubmit() {
      this.$refs.ruleForm.validate(async (valid) => {
        if (valid) {
          if (this.isEdit) {
            this.update()
          } else {
            this.create()
          }
        } else {
          return false
        }
      })
    },
    async create() {
      this.$emit('create', this.form)
      this.closeModal()
    },
    async update() {
      this.$emit('update', { ...this.form, uniqueId: this.selectedRule.uniqueId, id: this.selectedRule?.id || null })
      this.closeModal()
    },
    async deleteRule() {
      this.$emit('delete', this.selectedRule)
      this.closeModal()
    },
    clearForm() {
      this.form = {
        name: '',
        description: '',
        image: undefined,
        imageUrl: '',
      }
    },
    closeModal() {
      this.$emit('close')
      setTimeout(() => {
        this.clearForm()
      }, 500)
    },
  },
  mounted() {
    if (this.selectedRule) {
      this.templateDataFilling()
    }
  },
}
</script>

<style lang="scss">
.rule-modal {
  &__image-container {
    display: flex;
    & .ant-upload {
      width: 166px !important;
      height: 166px !important;
      margin: 0 !important;
    }
  }
  .footer {
    display: flex;
    justify-content: space-between;
    &::before,
    &::after {
      content: none;
    }
  }
}
</style>
